import { useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { useMutation } from '@apollo/client';
import * as yup from 'yup';
// API
import { SWITCH_TO_CREATOR } from 'api/auth/mutations';
import { CREATE_PRESIGNED_AVATAR_URL } from 'api/upload/mutations';
// Types
import { AccountSubType } from 'api/graphql-global-types';
import {
  SwitchToCreator,
  SwitchToCreatorVariables,
} from 'api/auth/types/SwitchToCreator';
import {
  CreatePresignedAvatarUrl,
  CreatePresignedAvatarUrlVariables,
} from 'api/upload/types/CreatePresignedAvatarUrl';
// Context
import { OnboardingFormData, SocialAccount } from 'context/OnboardingContext';
// Helpers
import { yupInternationalPhoneValidation } from 'helpers/validation';
import { isAthleteOrContentCreator } from 'helpers/accountType';
import { uploadImagesToS3 } from 'helpers/single-uploader';
import { createUsernameValidation } from 'helpers/usernameValidation';
import {
  addOtpFields,
  persistFormFields,
  shouldRevalidateOtp,
} from 'helpers/profile-setup';
import { getExtTalentBrandDealAttributionFromStorage } from 'helpers/extTalentBrandDeal';
import { getMatchupInviteTokenFromLocalStorage } from 'helpers/storage';
import { getSocialProfileLinkError } from 'helpers/socialMediaLink';
// Hooks
import { useGetCurrUser, useOnboardingContext } from 'hooks';
// Components
import FullScreenStepperModal, {
  FullScreenStepperModalFooter,
} from 'components/common3/FullScreenStepperModal/FullScreenStepperModal';
import ProfileStep from '../ProfileStep/ProfileStep';
import BasicInfoStep from '../BasicInfoStep/BasicInfoStep';
import SocialMediaStep from '../SocialMediaStep/SocialMediaStep';
import { showToast } from 'components/common/Toast/Toast';

export const legacyOnboardingValidationSchema = (step: number) => {
  if (step === 1) {
    return yup.object().shape({
      username: createUsernameValidation(),
      profileName: yup.string().required('Profile name is required'),
      avatar: yup.string().required('Avatar is required'),
    });
  }
  if (step === 2) {
    return yup.object().shape({
      accountType: yup.object().required('Account type is required'),
      experienceLevel: yup
        .object()
        .nullable()
        .when(['accountType'], {
          is: (accountType) => accountType?.value === AccountSubType.Athlete,
          then: yup
            .object()
            .shape({
              value: yup.string().required('Experience level is required'),
              label: yup.string().required('Experience level is required'),
            })
            .required('Experience level is required'),
        }),
      birthDate: yup
        .date()
        .nullable()
        .when(['accountType'], {
          is: (accountType) => isAthleteOrContentCreator(accountType),
          then: yup
            .date()
            .max(new Date(), 'Birthday cannot be in the future')
            .required('Birthday is required'),
        }),
      gender: yup
        .object()
        .nullable()
        .when(['accountType'], {
          is: (accountType) => isAthleteOrContentCreator(accountType),
          then: yup
            .object()
            .shape({
              value: yup.string().required('Gender is required'),
              label: yup.string().required('Gender is required'),
            })
            .required('Gender is required'),
        }),
      bio: yup.string().max(300, 'Bio must be less than 300 characters'),
      sport: yup.object().required('Sport is required'),
      phoneNumber: yupInternationalPhoneValidation(),
      phoneOptIn: yup
        .boolean()
        .oneOf([true])
        .required('This field is required'),
      location: yup.object().required('Location is required'),
    });
  }

  if (step === 3) {
    return yup.object().shape({
      socialAccounts: yup
        .array()
        .of(
          yup.object().shape({
            platform: yup.string().required('Platform is required'),
            link: yup
              .string()
              .test(
                'is-social-profile-link',
                'Link must point at your profile',
                function (value) {
                  const { platform } = (this.parent || {}) as SocialAccount;

                  // Blank after trim is "not typed". The helper returns no error for that,
                  // and yup required() still accepts a string of spaces.
                  if (!value?.trim()) {
                    return this.createError({ message: 'Link is required' });
                  }

                  const linkError = getSocialProfileLinkError(platform, value);

                  return linkError
                    ? this.createError({ message: linkError })
                    : true;
                }
              )
              .required('Link is required'),
          })
        )
        .required('At least one social account is required'),
    });
  }

  return yup.object().shape({});
};

const LegacyOnboarding = () => {
  const { data, loading: userDataLoading } = useGetCurrUser();

  const {
    otpValidation,
    setShowCreatorSetupModal,
    setShowOTPModal,
    setOTPValidation,
  } = useOnboardingContext();

  const [switchToCreator, { loading: loadingSwitchToCreator }] = useMutation<
    SwitchToCreator,
    SwitchToCreatorVariables
  >(SWITCH_TO_CREATOR, {
    onCompleted: () => {
      setOTPValidation(null);
      handleModalClose();
      if (
        !getExtTalentBrandDealAttributionFromStorage() &&
        !getMatchupInviteTokenFromLocalStorage()
      ) {
        setShowCreatorSetupModal(true);
      }
    },
  });
  const [
    createPresignedAvatarUrl,
    { loading: loadingCreatePresignedUrl },
  ] = useMutation<CreatePresignedAvatarUrl, CreatePresignedAvatarUrlVariables>(
    CREATE_PRESIGNED_AVATAR_URL
  );

  const loading = loadingSwitchToCreator || loadingCreatePresignedUrl;

  const {
    onboardingStep,
    showOnboardingModal,
    onboardingData,
    setShowOnboardingModal,
    setOnboardingStep,
    resetOnboardingStateAfterSwitch,
    setOnboardingData,
  } = useOnboardingContext();

  const methods = useForm<OnboardingFormData>({
    resolver: yupResolver(legacyOnboardingValidationSchema(onboardingStep)),
    defaultValues: {
      username: '',
      profileName: '',
      avatar: '',
      accountType: undefined,
      experienceLevel: undefined,
      sport: undefined,
      birthDate: null,
      gender: undefined,
      bio: '',
      phoneNumber: '',
      phoneOptIn: false,
      location: undefined,
      socialAccounts: [
        {
          platform: undefined,
          link: '',
          isAdded: false,
        },
      ],
      email: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });

  useEffect(() => {
    if (data?.me) {
      methods.reset({
        username: onboardingData?.username || data?.me?.slug || '',
        profileName:
          onboardingData?.profileName ||
          `${data?.me?.firstName || ''} ${data?.me?.lastName || ''}`.trim(),
        avatar: onboardingData?.avatar || data?.me?.avatarUrl || '',
        phoneNumber: data?.me?.phoneNumber || '',
        phoneOptIn: data?.me?.phoneOptIn || false,
        email: data?.me?.email || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleModalClose = useCallback(() => {
    setShowOnboardingModal(false);
    resetOnboardingStateAfterSwitch();
    methods.reset({});
  }, [setShowOnboardingModal, resetOnboardingStateAfterSwitch, methods]);

  useEffect(() => {
    if (!userDataLoading && showOnboardingModal && !data?.me) {
      handleModalClose();
    }
  }, [data, userDataLoading, showOnboardingModal, handleModalClose]);

  const handlePrevStepClick = useCallback(() => {
    if (onboardingStep > 1) {
      const fieldsToPersist = [
        'username',
        'avatar',
      ] as (keyof OnboardingFormData)[];
      const updatedFields = persistFormFields<OnboardingFormData>(
        methods,
        onboardingData,
        fieldsToPersist
      );
      setOnboardingData(updatedFields);
      setOnboardingStep(onboardingStep - 1);
    }
    // onboardingData isn't needed here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingStep, setOnboardingStep]);

  const handleSubmit = methods.handleSubmit(
    async (values) => {
      try {
        // Check if we need to show OTP modal for phone number
        if (onboardingStep === 2 && values.email && values.phoneNumber) {
          const {
            phoneNeedsVerification,
            emailNeedsVerification,
          } = shouldRevalidateOtp(
            values.email,
            values.phoneNumber,
            data?.me,
            true
          );

          if (
            emailNeedsVerification &&
            phoneNeedsVerification &&
            !otpValidation
          ) {
            setShowOTPModal(true);
            return;
          }
        }

        if (onboardingStep === 1) {
          setOnboardingData(values);
          setOnboardingStep(2);
        }

        if (onboardingStep === 2) {
          setOnboardingData(values);
          setOnboardingStep(3);
        }

        if (onboardingStep === 3) {
          if (
            onboardingData &&
            onboardingData.phoneNumber &&
            onboardingData.accountType &&
            onboardingData.username &&
            onboardingData.profileName
          ) {
            let avatarKey = '';
            if (
              onboardingData.avatar &&
              typeof onboardingData.avatar !== 'string'
            ) {
              const filenameParts = onboardingData.avatar.name.split('.');

              const {
                data: presignedUrlsData,
              } = await createPresignedAvatarUrl({
                variables: {
                  input: {
                    ext: filenameParts[filenameParts.length - 1],
                    contentType: onboardingData.avatar.type,
                  },
                },
              });

              if (presignedUrlsData?.createPresignedAvatarUrl) {
                await uploadImagesToS3([
                  {
                    ...presignedUrlsData.createPresignedAvatarUrl,
                    file: onboardingData.avatar,
                  },
                ]);

                avatarKey = presignedUrlsData.createPresignedAvatarUrl.key;
              }
            }

            await switchToCreator({
              variables: {
                input: {
                  username: onboardingData.username,
                  profileName: onboardingData.profileName,
                  ...(avatarKey && { avatarKey }),
                  ...(onboardingData.bio && { bio: onboardingData.bio }),
                  ...(onboardingData.location && {
                    country: onboardingData.location.countryCode,
                    city: onboardingData.location.city,
                    ...(onboardingData.location.state && {
                      state: onboardingData.location.stateCode,
                    }),
                    location: onboardingData.location.coordinates,
                  }),
                  ...(onboardingData.experienceLevel && {
                    level: onboardingData.experienceLevel.value,
                  }),
                  ...(onboardingData.birthDate && {
                    birthDate: dayjs(onboardingData.birthDate).format(
                      'YYYY-MM-DD'
                    ),
                  }),
                  ...(onboardingData.gender && {
                    gender: onboardingData.gender.value,
                  }),
                  sportIds: onboardingData.sport
                    ? [onboardingData.sport.id]
                    : [],
                  subtype: onboardingData.accountType.value,
                  phoneNumberE164: onboardingData.phoneNumber as string,
                  // TODO: check with PROD if this is okay not be shown in form
                  phoneOptIn: true,
                  ...(values.socialAccounts && {
                    socialAccounts: values.socialAccounts.map(
                      (socialAccount) => ({
                        platform: socialAccount.platform,
                        link: socialAccount.link.trim(),
                      })
                    ),
                  }),
                  ...addOtpFields(otpValidation),
                },
              },
            });

            handleModalClose();
          }
        }
      } catch (error) {
        setOTPValidation(null);
        console.error(error);
        showToast({
          type: 'error',
          message: error?.message || 'Something went wrong. Please try again.',
        });
      }
    },
    (err) => console.log(err)
  );

  const getModalFooter = (): FullScreenStepperModalFooter | undefined => {
    if (onboardingStep === 1 || onboardingStep === 2 || onboardingStep === 3) {
      return {
        totalStepsNumber: 4,
        stepNumber: onboardingStep,
        submitButtonProps: {
          onClick: handleSubmit,
          variant: 'primary',
          loading,
        },
        submitButtonText: 'Next',
        showPrevButton: true,
        prevButtonText: 'Back',
        prevButtonProps: {
          onClick: onboardingStep > 1 ? handlePrevStepClick : handleModalClose,
          variant: 'secondary',
        },
      };
    }

    return;
  };

  return (
    <FullScreenStepperModal
      open={showOnboardingModal}
      onClose={handleModalClose}
      title="JOIN AS A CREATOR"
      footer={getModalFooter()}
    >
      <FormProvider {...methods}>
        {onboardingStep === 1 && <ProfileStep />}

        {onboardingStep === 2 && <BasicInfoStep />}

        {onboardingStep === 3 && <SocialMediaStep />}
      </FormProvider>
    </FullScreenStepperModal>
  );
};

export default LegacyOnboarding;
